import React from "react";
import axios from "axios";
import { Star, Check, Globe, Rocket, CheckCircle2 } from "lucide-react";
import SiteNavbar from "@/components/SiteNavbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  {
    id: "Free Website & Marketing Audit",
    icon: Globe,
    title: "Free Website & Marketing Audit",
    desc: "For businesses already trading online. We'll audit your website, SEO, and marketing and show you exactly where you're losing leads.",
  },
  {
    id: "Free Business Plan",
    icon: Rocket,
    title: "Free Business Plan",
    desc: "For new businesses or anyone without an online presence. We'll put together a full plan to get you online and generating leads.",
  },
];

export default function Audit() {
  const [option, setOption] = React.useState(OPTIONS[0].id);
  const [form, setForm] = React.useState({
    name: "",
    business: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/audit`, { option, ...form });
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead");
      }
      setSent(true);
    } catch (err) {
      setError(
        "Sorry, something went wrong sending your request. Please try again, or call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1f3d] text-white">
      <SiteNavbar />

      <main className="mx-auto max-w-4xl px-5 py-14 sm:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/80">
          <Star className="h-4 w-4 text-[#e9a31e]" />
          Free Audit
        </div>

        <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-6xl">
          Request Your Free <span className="text-[#e9a31e]">Audit</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-white/70">
          Choose the option that fits your business below and we'll get back to
          you within 24 hours.
        </p>

        {sent ? (
          <Reveal>
            <div className="mt-12 flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e9a31e]/15 text-[#e9a31e]">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="mt-5 text-2xl font-bold">
                Thanks — request received!
              </h3>
              <p className="mt-2 max-w-md text-white/70">
                Your <strong>{option}</strong> request is on its way to our
                team. We'll be in touch within 24 hours.
              </p>
            </div>
          </Reveal>
        ) : (
          <>
            {/* Option cards */}
            <Reveal>
              <div className="mt-12 grid gap-5 md:grid-cols-2">
                {OPTIONS.map((o) => {
                  const Icon = o.icon;
                  const active = option === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setOption(o.id)}
                      aria-pressed={active}
                      className={`relative flex flex-col rounded-2xl border p-7 text-left transition-all duration-200 ${
                        active
                          ? "border-[#e9a31e] bg-[#e9a31e]/10 shadow-lg shadow-black/20"
                          : "border-white/10 bg-white/[0.03] hover:border-[#e9a31e]/40"
                      }`}
                    >
                      <span
                        className={`absolute right-5 top-5 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                          active
                            ? "border-[#e9a31e] bg-[#e9a31e] text-[#0a1f3d]"
                            : "border-white/30 text-transparent"
                        }`}
                      >
                        <Check className="h-4 w-4" />
                      </span>
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e9a31e]/15 text-[#e9a31e]">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="mt-5 text-xl font-bold">{o.title}</span>
                      <span className="mt-2 text-white/70">{o.desc}</span>
                    </button>
                  );
                })}
              </div>
            </Reveal>

            {/* Form */}
            <Reveal>
              <form
                onSubmit={handleSubmit}
                className="mt-8 space-y-5 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Full Name"
                    placeholder="Jane Smith"
                    required
                    value={form.name}
                    onChange={update("name")}
                  />
                  <Field
                    label="Business Name"
                    placeholder="Acme Ltd"
                    value={form.business}
                    onChange={update("business")}
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Email Address"
                    type="email"
                    placeholder="jane@acme.co.uk"
                    required
                    value={form.email}
                    onChange={update("email")}
                  />
                  <Field
                    label="Phone Number"
                    type="tel"
                    placeholder="07…"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/80">
                    Tell us about your business
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="What do you do, and what would you love to improve?"
                    className="w-full rounded-lg border border-white/15 bg-[#081831] px-4 py-3 text-white placeholder:text-white/35 outline-none transition-colors focus:border-[#e9a31e]"
                  />
                </div>
                {error && (
                  <p className="text-sm font-medium text-red-400">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? "Sending…" : "Send My Request"}
                </button>
              </form>
            </Reveal>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, type = "text", placeholder, required, value, onChange }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-white/80">
        {label}
        {required && <span className="text-[#e9a31e]"> *</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-white/15 bg-[#081831] px-4 py-3 text-white placeholder:text-white/35 outline-none transition-colors focus:border-[#e9a31e]"
      />
    </div>
  );
}
